import { get, isNull } from "lodash";
import { Controller, UseFormReturn } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { UseUploadFileReturn } from "src/api/filesApi/queries";
import {
  ReviewCaseResponseData,
  ReviewCaseResponseSchema,
  ReviewCaseResponseSchemaProperty,
} from "src/api/types";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Callout } from "src/design-system/Callout";
import { Tooltip } from "src/design-system/Tooltip";
import { FileDropzone } from "src/manualReview/FileDropzone";
import { ManualReviewDescriptionEditor } from "src/manualReviewNode/ManualReviewDescriptionEditor";

export const MR_FORM_ELEMENTS_PREFIX = "mr_form_elements_prefix";
export const Form: React.FC<{
  onClickSubmit: () => void;
  disabled: boolean;
  disabledReason?: string;
  schema: ReviewCaseResponseSchema;
  isPreview?: boolean;
  form: UseFormReturn<ReviewCaseResponseData>;
  error?: string;
  onChange: () => void;
  fileUpload: UseUploadFileReturn;
}> = ({
  onClickSubmit,
  disabled,
  disabledReason,
  schema,
  form,
  isPreview,
  onChange,
  error,
  fileUpload,
}) => {
  const order = schema.order ?? Object.keys(schema.properties);
  const orderedProperties = order.map(
    (key) => [key, schema.properties[key]] as const,
  );

  return (
    <form
      className={twJoin("relative", !error && "pt-7")}
      data-loc="manual-review-response-form"
      onChange={onChange}
    >
      {error && (
        <div className="sticky top-0 right-0 left-0 z-10 bg-white py-7">
          <Callout type="error">{error}</Callout>
        </div>
      )}
      {orderedProperties.map(([responseKey, responseProperty]) => {
        const isRequired =
          schema.required.includes(responseKey) && "This field is required";

        return (
          <FormItem
            key={responseKey}
            dataLoc={`manual-review-form-${responseKey}`}
            gap="sm"
            id={`${MR_FORM_ELEMENTS_PREFIX}_${responseKey}`}
            isRequired={!!isRequired}
            label={responseProperty.description}
          >
            <FormField
              disabled={disabled}
              fileUpload={fileUpload}
              form={form}
              isPreview={!!isPreview}
              isRequired={isRequired}
              responseKey={responseKey}
              responseProperty={responseProperty}
              onFormChange={onChange}
            />
            {get(form.formState.errors, responseKey) && (
              <ErrorHint>
                {get(form.formState.errors, responseKey)?.message}
              </ErrorHint>
            )}
          </FormItem>
        );
      })}
      <div className="sticky bottom-0 -mt-6 bg-white py-7">
        <Tooltip
          disabled={!disabledReason}
          placement="top"
          title={disabledReason}
          asChild
        >
          <Button
            dataLoc="manual-review-form-submit"
            disabled={disabled}
            fullWidth
            onClick={onClickSubmit}
          >
            Submit
          </Button>
        </Tooltip>
      </div>
    </form>
  );
};

const FormField: React.FC<{
  responseProperty: ReviewCaseResponseSchemaProperty;
  responseKey: string;
  disabled: boolean;
  isRequired: string | false;
  fileUpload: UseUploadFileReturn;
  form: UseFormReturn<ReviewCaseResponseData>;
  isPreview: boolean;
  onFormChange: () => void;
}> = ({
  responseProperty,
  responseKey,
  disabled,
  isRequired,
  fileUpload,
  form,
  isPreview,
  onFormChange,
}) => {
  if ("enum" in responseProperty) {
    return (
      <Controller
        control={form.control}
        name={responseKey}
        render={({ field }) => {
          const selectProps = {
            dataLoc: "manual-review-form-select",
            disabled,
            errored: !!get(form.formState.errors, responseKey),
            options: responseProperty.enum.map((option, i) => ({
              key: i.toString(),
              value: option,
            })),
            placeholder: "Select option",
            value: field.value
              ? responseProperty.enum
                  .indexOf(field.value as string | number)
                  .toString()
              : null,
          };

          // Split into two separate cases, otherwise typescript will complain about the onChange function
          return !isRequired ? (
            <Select
              {...selectProps}
              showResetButton
              onChange={(key) => {
                const isChosenValue =
                  key && responseProperty.enum[parseInt(key)] === field.value;

                return field.onChange(
                  isNull(key) || isChosenValue
                    ? null
                    : responseProperty.enum[parseInt(key)],
                );
              }}
            />
          ) : (
            <Select
              {...selectProps}
              onChange={(key) => {
                const isChosenValue =
                  key && responseProperty.enum[parseInt(key)] === field.value;
                return field.onChange(
                  isChosenValue
                    ? undefined
                    : responseProperty.enum[parseInt(key)],
                );
              }}
            />
          );
        }}
        rules={{
          required: isRequired,
        }}
      />
    );
  }

  if (responseProperty.type === "boolean") {
    return (
      <Controller
        control={form.control}
        name={responseKey}
        render={({ field }) => (
          <SimpleRadioGroup
            className="space-x-4"
            disabled={disabled}
            orientation="horizontal"
            value={field.value?.toString()}
            onValueChange={(value) => field.onChange(value === "true")}
          >
            <SimpleRadioGroup.Item
              disabled={disabled}
              label="Yes"
              labelClassName="ml-1"
              value="true"
            />
            <SimpleRadioGroup.Item
              disabled={disabled}
              label="No"
              labelClassName="ml-1"
              value="false"
            />
          </SimpleRadioGroup>
        )}
        rules={{
          validate: (value) => {
            if (isRequired && value === undefined) return isRequired;
            else return true;
          },
        }}
      />
    );
  }

  if (
    responseProperty.type === "string" &&
    responseProperty.$comment === "file_upload"
  ) {
    return (
      <Controller
        control={form.control}
        name={responseKey}
        render={({ field, fieldState }) => {
          return (
            <>
              <FileDropzone
                key={field.name}
                data={fileUpload.getFile(field.name)}
                disabled={disabled || isPreview}
                isError={!!fieldState.error}
                isUploading={fileUpload.isLoading}
                onFileNameChange={(name) => {
                  fileUpload.updateFileName(name, field.name);
                }}
                onFileRemove={() => {
                  fileUpload.onFileRemove(field.name);
                  field.onChange(null);
                }}
                onFileSelect={(file) => {
                  fileUpload.onFileSelect(file, field.name);
                  setTimeout(() => form.trigger(field.name), 0);
                }}
              />
            </>
          );
        }}
        rules={{
          validate: () => {
            if (isRequired && !fileUpload.getFile(responseKey)) {
              return "This file is required";
            }
            return true;
          },
        }}
      />
    );
  }

  if (
    responseProperty.type === "string" &&
    responseProperty.$comment === "textarea"
  ) {
    return (
      <Controller
        control={form.control}
        name={responseKey}
        render={({ field }) => (
          <ManualReviewDescriptionEditor
            disabled={disabled}
            placeholder={`Enter ${responseProperty.description}`}
            value={field.value ? String(field.value) : ""}
            onChange={(value) => {
              // Trigger form onChange manually, since quill doesn't trigger it
              onFormChange();

              field.onChange(value);
            }}
          />
        )}
        rules={{
          required: isRequired,
        }}
      />
    );
  }

  return (
    <Input
      data-loc="manual-review-form-input"
      disabled={disabled}
      errored={!!get(form.formState.errors, responseKey)}
      placeholder={`Enter ${responseProperty.description}`}
      fullWidth
      {...form.register(responseKey, {
        required: isRequired,
        validate: (value) => {
          if (value === undefined) return true;
          else if (
            typeof value === "string" &&
            !isRequired &&
            value.trim() === ""
          )
            return true;
          else if (responseProperty.type === "string") return true;
          else if (
            responseProperty.type === "integer" &&
            !Number.isInteger(parseFloat(value as string))
          ) {
            return `This field must contain an integer`;
          } else if (
            responseProperty.type === "number" &&
            isNaN(parseFloat(value as string))
          ) {
            return `This field must contain a number`;
          } else return true;
        },
      })}
    />
  );
};
